// Privacy policy (Jeffrey, 2026-08-30): "copy what they had before but clean it
// up and put it on the website and bury it at the bottom."
//
// The previous site's policy (recovered from the Internet Archive — see
// docs/legal-archive/) was Shopify's stock template: it described purchases,
// credit-card storage, PCI-DSS and Shopify's own cookies, none of which this
// site does. Its structure, its Section 2 consent wording and its contact route
// are kept; everything about running a store is gone.
//
// EVERY factual statement below was checked against the code, not assumed:
//   collection      — apps/web/api/apply.js + pages/V2FormReplica.tsx
//   partial rows    — status='partial', written at the contact step
//   forwarding      — api/apply.js relays submissions to the operations intake
//   attribution     — lib/attribution.ts, STORE_KEY, 90 days
//   landing         — lib/attribution.ts, LANDING_KEY, 90 days (CMSY-67)
//   draft           — lib/storageKeys.ts, DRAFT_TTL_MS
// The page describes these by category and no longer renders the identifiers,
// so it does not import them. Privacy.test.tsx still does, and asserts their
// ABSENCE — the guard moved from "the names must be right" to "there must be
// no names", which is the weaker claim the trimmed policy can actually keep.
//   session replay  — lib/posthog.ts, session_recording maskAllInputs: true
//   enhanced convs  — V2FormReplica.tsx gtag('set','user_data',{email,phone})
//   ad features     — GA4 Admin API, property 500698529: googleSignalsSettings
//                     state=GOOGLE_SIGNALS_ENABLED (read 2026-09-18, not relayed)
//   data residency  — Supabase project ca-central-1
// If any of those change, this page changes with them.
//
// ⚠️ 2026-09-17: that promise had been broken for two weeks. This file named
// ApplicationForm.tsx, deleted at the 2026-09-02 relaunch, as its evidence; §6
// disclosed that form's `cc_application_draft_v1`, which nothing writes any
// more; and `cc_landing_v1` (added 2026-09-10) was collected and never
// disclosed. Privacy.test.tsx had hard-coded the same wrong key, so the test
// guarding the promise was drifting with it. The key names below are now
// IMPORTED from the modules that write them — prose can still go stale, but a
// named key cannot.
import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <SEO
        title="Privacy Policy | Commissary Connect"
        description="How Commissary Connect collects, uses and protects the personal information you provide through this website."
      />
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: 18 September 2026</p>

        <p>
          This policy explains what personal information Commissary Connect collects through
          this website, why we collect it, who we share it with, and how you can see it or
          have it deleted. It applies to commissaryconnect.com. We are based in Vancouver,
          British Columbia, and we handle personal information in accordance with British
          Columbia&rsquo;s <em>Personal Information Protection Act</em>.
        </p>

        <h2>1. What we collect</h2>
        <h3>Information you give us</h3>
        <p>
          When you fill in our kitchen application, we collect your name, your contact
          details, and information about your business and the kitchen space you need.
        </p>
        <p>
          <strong>If you start an application and do not finish it, we keep what you entered.</strong>{' '}
          Once you complete the contact step, that information is saved so we can pick the
          conversation up with you. You can ask us to delete it at any time &mdash; see
          section 8.
        </p>
        <h3>Information collected automatically</h3>
        <p>
          When you browse the site we receive your device&rsquo;s internet protocol (IP)
          address, which tells us your approximate location, along with your browser and
          operating system, the pages you view and how you move through them.
        </p>
        <h3>Advertising information</h3>
        <p>
          If you reach us from an advertisement or a campaign link, we store the campaign
          identifiers from that link (<code>utm_source</code>, <code>utm_medium</code>,{' '}
          <code>utm_campaign</code>, <code>utm_content</code>, <code>utm_term</code> and
          Google&rsquo;s <code>gclid</code>) in your browser for 90 days, so that we can tell
          which advertising actually reaches people. If you go on to apply, those identifiers
          are stored alongside your application.
        </p>
        <p>
          Separately, the first time you arrive we record how you got here &mdash; the website
          that referred you, or &ldquo;direct&rdquo; if you came straight to us &mdash; along
          with the first page you opened and the time. This is kept in your browser for 90
          days and is never overwritten, so that a visit which carried no campaign identifiers
          can still be counted. It too is stored alongside your application if you apply.
        </p>

        <h2>2. How we use it</h2>
        <ul>
          <li>To process your application and contact you about it.</li>
          <li>To answer questions you send us by email or phone.</li>
          <li>To operate, secure and improve this website.</li>
          <li>To measure which advertising and which pages bring people to us.</li>
        </ul>
        <p>We do not sell your personal information.</p>

        <h2>3. Consent</h2>
        <p>
          <strong>How do we get your consent?</strong> When you provide personal information
          to apply for kitchen space or to ask us a question, we take it that you consent to
          our collecting it and using it for that specific reason.
        </p>
        <p>
          If we want to use your information for a secondary reason, such as marketing, we
          will either ask you for your express consent or give you a clear opportunity to say
          no.
        </p>
        <p>
          <strong>How do you withdraw your consent?</strong> If you change your mind, you can
          withdraw your consent to our contacting you, or to our continued collection, use or
          disclosure of your information, at any time. Email{' '}
          <a href="mailto:info@commissaryconnect.com">info@commissaryconnect.com</a> or write
          to us at the address in section 10.
        </p>

        <h2>4. Session recording and analytics</h2>
        <p>
          We use analytics tools that record how visitors use the site, including a playback
          of your visit &mdash; the pages you saw, where you scrolled, and where you tapped or
          clicked. <strong>Text you type into form fields is masked and is not captured in
          those recordings</strong>; the information you enter in the application reaches us
          only when you submit it or complete the contact step, as described in section 1.
        </p>
        <p>
          We use these recordings and aggregate statistics to find the places where the site
          is confusing or broken. We do not use them to build a profile of you for
          advertising.
        </p>

        <h2>5. Who we share it with</h2>
        <p>
          We share personal information only with the service providers we need to run the
          business and this website, and only to the extent they need it:
        </p>
        <ul>
          <li>
            <strong>Commissary Connect&rsquo;s own operations system</strong> &mdash; your
            completed application is passed to the system our team uses to review applications
            and arrange kitchen space. Applications that you started but did not finish are
            <em> not</em> passed on.
          </li>
          <li>
            <strong>Supabase</strong> &mdash; database hosting.
          </li>
          <li>
            <strong>Vercel</strong> &mdash; website hosting, along with traffic and
            performance measurement.
          </li>
          <li>
            <strong>Google</strong> (Analytics and Ads) &mdash; measurement of site traffic and
            advertising performance. When you submit an application, your email address and
            phone number are also provided to Google in encoded form so that it can match the
            application to an advertisement you clicked.
          </li>
          <li>
            <strong>PostHog</strong> &mdash; the product analytics and session recording
            described in section 4.
          </li>
        </ul>
        <p>
          We may also disclose personal information if we are required by law to do so.
        </p>
        <p>
          Some of these providers are located in, or store data in, the United States. Where
          that is the case, your information may be subject to the laws of that country,
          including lawful requests by its authorities.
        </p>

        <h2>6. Cookies and browser storage</h2>
        <p>This site stores the following in your own browser:</p>
        <ul>
          <li>
            The campaign identifiers and referral information described in section 1, so that
            we can tell which advertising and which pages bring people to us.
          </li>
          <li>
            A draft of an application you have started, so that you do not lose your place if
            you leave and come back. It is cleared as soon as you submit.
          </li>
          <li>
            Cookies set by Google, PostHog and Vercel for the measurement purposes described
            above, including the Google advertising cookies described in section 7.
          </li>
        </ul>
        <p>
          You can clear or block these through your browser settings. Blocking them will not
          stop you using the site, though a saved draft will not be restored.
        </p>

        <h2>7. Google Analytics advertising features</h2>
        <p>
          We use Google Analytics advertising features, including Google signals, remarketing,
          and demographics and interests reporting. With these turned on, Google Analytics may
          associate your visit with the information Google holds for your Google account, if
          you are signed in to Google and have allowed that association in your own Google
          settings.
        </p>
        <p>
          To do this, Google Analytics&rsquo; own first-party cookies and Google&rsquo;s
          third-party advertising cookies are read together.
        </p>
        <p>
          You can opt out at any time.{' '}
          <a href="https://myadcenter.google.com/" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>{' '}
          controls ads personalisation on your Google account, and the{' '}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            Google Analytics opt-out add-on
          </a>{' '}
          stops Analytics collecting anything from your browser. Neither will stop you using
          this site.
        </p>

        <h2>8. Seeing, correcting or deleting your information</h2>
        <p>
          You can ask us what personal information we hold about you, ask us to correct it, or
          ask us to delete it. Email{' '}
          <a href="mailto:info@commissaryconnect.com">info@commissaryconnect.com</a> and we
          will respond within the time British Columbia law allows. We keep application records
          for as long as we need them for the purpose they were collected for and to meet our
          legal and accounting obligations, and we delete them on request unless we are
          required to keep them.
        </p>

        <h2>9. Security</h2>
        <p>
          We take reasonable precautions and follow accepted practices to protect personal
          information from loss, misuse, unauthorised access, disclosure, alteration and
          destruction. Application records are held in a database that is not publicly
          readable and is reachable only by our own systems. No method of transmission or
          storage on the internet is completely secure, and we cannot guarantee absolute
          security.
        </p>

        <h2>10. Contact us</h2>
        <p>
          Questions about this policy, or about how we handle your information, can go to{' '}
          <a href="mailto:info@commissaryconnect.com">info@commissaryconnect.com</a>, by phone
          to <a href="tel:7785587272" data-phone-label="privacy_policy">778.558.7272</a>, or by
          mail to:
        </p>
        {/* Registered office, per Jeffrey (2026-08-30) — chosen over the operating
            address at 401 Industrial Avenue that the old policy used. Source is
            the archived Terms of Use; see docs/legal-archive/. */}
        <address>
          Commissary Connect
          <br />
          602&ndash;1212 Howe Street
          <br />
          Vancouver, BC V6Z 2M9
          <br />
          Canada
        </address>

        <h2>11. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. If we do, we will change the
          &ldquo;last updated&rdquo; date at the top of this page. Where a change materially
          affects how we use information we have already collected, we will take reasonable
          steps to tell you.
        </p>

        <p className="legal-back">
          <Link to="/">Back to home</Link>
        </p>
      </div>
    </div>
  );
};

export default Privacy;
